import axios from 'axios';
import { API_BASE_URL } from '../config';

const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';

const authApi = axios.create({
  baseURL: API_BASE_URL,
});

/**
 * Limpia la sesión local sin notificar al backend
 * (Usado cuando el token ya expiró y no se puede hacer logout en el servidor)
 */
export function clearSession() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
}

/**
 * Login con email y contraseña
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{user: LoginResponse, token: string}>}
 */
export async function login(email, password) {
  try {
    const response = await authApi.post('Auth/login', {
      email: email.trim(),
      password,
    });

    // MFA pendiente: el backend envió el código por email y aún no hay token.
    // Devolvemos la respuesta para que el caller (AuthContext/Login.jsx) muestre el input del código.
    if (response.data && response.data.requiresMfa) {
      return response.data;
    }

    if (response.data && response.data.token) {
      // Guardar token y usuario
      localStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
      localStorage.setItem(
        AUTH_USER_KEY,
        JSON.stringify({
          userId: response.data.userId,
          name: response.data.name,
          email: response.data.email,
          role: response.data.role,
          expiresAt: response.data.expiresAt,
          mustChangePassword: response.data.mustChangePassword ?? false,
        })
      );

      return response.data;
    }

    throw new Error('No se recibió token en la respuesta');
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Error al iniciar sesión';
    const customError = new Error(message);
    customError.originalError = error;
    throw customError;
  }
}

/**
 * Verifica el código MFA enviado al email y completa el login.
 * @param {number} userId
 * @param {string} code
 * @returns {Promise<LoginResponse>}
 */
export async function verifyMfa(userId, code) {
  try {
    const response = await authApi.post('Auth/verify-mfa', {
      userId,
      code: String(code).trim(),
    });

    if (response.data && response.data.token) {
      localStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
      localStorage.setItem(
        AUTH_USER_KEY,
        JSON.stringify({
          userId: response.data.userId,
          name: response.data.name,
          email: response.data.email,
          role: response.data.role,
          expiresAt: response.data.expiresAt,
          mustChangePassword: response.data.mustChangePassword ?? false,
        })
      );
      return response.data;
    }

    throw new Error('No se recibió token tras verificar el código');
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Código inválido o expirado';
    const customError = new Error(message);
    customError.originalError = error;
    throw customError;
  }
}

/**
 * Valida si el token actual es válido
 * @returns {Promise<boolean>}
 */
export async function validateToken() {
  try {
    const token = getToken();
    if (!token) return false;

    const response = await authApi.post('Auth/validate', token);
    return response.data?.isValid ?? false;
  } catch (error) {
    console.error('Error validando token:', error);
    return false;
  }
}

/**
 * Refresca el token actual
 * @returns {Promise<{user: LoginResponse, token: string}>}
 */
export async function refreshToken() {
  try {
    const token = getToken();
    if (!token) throw new Error('No hay token para refrescar');

    const response = await authApi.post(
      'Auth/refresh',
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.token) {
      localStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
      localStorage.setItem(
        AUTH_USER_KEY,
        JSON.stringify({
          userId: response.data.userId,
          name: response.data.name,
          email: response.data.email,
          role: response.data.role,
          expiresAt: response.data.expiresAt,
        })
      );
      return response.data;
    }

    throw new Error('No se recibió token en el refresh');
  } catch (error) {
    clearSession();
    throw error;
  }
}

/**
 * Logout - notifica al backend y limpia token y datos del usuario
 * @returns {Promise<void>}
 */
export async function logout() {
  try {
    const token = getToken();
    if (token) {
      await authApi.post(
        'Auth/logout',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    }
  } catch (error) {
    console.error('Error al cerrar sesión en el servidor:', error);
  } finally {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
  }
}

/**
 * Obtiene el token actual
 * @returns {string | null}
 */
export function getToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

/**
 * Obtiene los datos del usuario actual
 * @returns {object | null}
 */
export function getCurrentUser() {
  const user = localStorage.getItem(AUTH_USER_KEY);
  return user ? JSON.parse(user) : null;
}

/**
 * Verifica si el usuario está autenticado
 * @returns {boolean}
 */
export function isAuthenticated() {
  return !!getToken();
}

/**
 * Cambia la contraseña del usuario autenticado
 * @param {string} oldPassword
 * @param {string} newPassword
 * @param {AbortSignal} [signal]
 * @returns {Promise<object>}
 */
export async function changePassword(oldPassword, newPassword, signal) {
  const token = getToken();
  if (!token) throw new Error('No hay token para cambiar contraseña');

  const response = await authApi.post(
    'Auth/change-password',
    { oldPassword, newPassword },
    { headers: { Authorization: `Bearer ${token}` }, signal }
  );
  return response.data;
}

export const recoverPassword = async (email) => {
  if (!email || typeof email !== 'string') {
    throw new Error('Email is required.');
  }

  try {
    const payload = { email: email.trim() };

    const response = await authApi.post(
      `${API_BASE_URL}/auth/recover-password`,
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    );

    return response.data;
  } catch (error) {
    console.error(
      'Error calling recoverPassword:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export default {
  login,
  validateToken,
  refreshToken,
  logout,
  clearSession,
  getToken,
  getCurrentUser,
  isAuthenticated,
  changePassword,
};
